/**
 * usePatrolLocationsApi Hook
 *
 * React Query hook for fetching patrol locations from the API with filtering.
 * Used when scope is 'today' or 'all_sites' to fetch locations beyond real-time guards.
 *
 * Note: For 'active' scope, the existing usePatrolLocations hook derives data from
 * guards in real-time. This hook is for the new API-based data sources.
 *
 * Features:
 * - Supports date and scope filtering
 * - 30-second stale time for caching
 * - Automatic refetch on filter changes
 */

import { useQuery } from '@tanstack/react-query';
import {
  fetchPatrolLocations,
  PatrolLocationsApiResponse,
  PatrolLocationScope,
  PatrolLocationSummary,
} from '../../../../../api/dispatch';

/** Query key for patrol locations API */
export const PATROL_LOCATIONS_API_QUERY_KEY = ['patrolLocationsApi'];

/** Stale time (30 seconds) */
const STALE_TIME = 30 * 1000;

export interface UsePatrolLocationsApiOptions {
  /** Date to fetch patrols for (YYYY-MM-DD, defaults to today) */
  date?: string;
  /** Scope filter: 'active' | 'today' | 'all_sites' */
  scope?: PatrolLocationScope;
  /** Whether to enable fetching (default: true) */
  enabled?: boolean;
}

export interface UsePatrolLocationsApiResult {
  /** Patrol locations from API */
  locations: PatrolLocationSummary[];
  /** The date used for the query */
  date: string | null;
  /** The scope used for the query */
  scope: PatrolLocationScope | null;
  /** Total count of locations */
  totalCount: number;
  /** Whether initial load is in progress */
  isLoading: boolean;
  /** Whether any fetch is in progress */
  isFetching: boolean;
  /** Whether an error occurred */
  isError: boolean;
  /** Error object if present */
  error: Error | null;
  /** Manually trigger a refresh */
  refetch: () => void;
}

/**
 * Hook to fetch patrol locations from the API with filtering
 *
 * @param options - Configuration options
 * @returns Patrol locations with loading/error states
 */
export function usePatrolLocationsApi(
  options: UsePatrolLocationsApiOptions = {}
): UsePatrolLocationsApiResult {
  const { date, scope = 'today', enabled = true } = options;

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery<PatrolLocationsApiResponse>({
    queryKey: [...PATROL_LOCATIONS_API_QUERY_KEY, { date, scope }],
    queryFn: async () => {
      try {
        return await fetchPatrolLocations({ date, scope });
      } catch (err) {
        console.error('[usePatrolLocationsApi] Failed to fetch:', err);
        throw err;
      }
    },
    enabled,
    staleTime: STALE_TIME,
    // Return empty array on error rather than crashing
    placeholderData: { locations: [], date: '', scope: 'today', totalCount: 0 },
  });

  // Safely extract data with null checks
  const locations = Array.isArray(data?.locations) ? data.locations : [];

  return {
    locations,
    date: data?.date ?? null,
    scope: data?.scope ?? null,
    totalCount: data?.totalCount ?? 0,
    isLoading,
    isFetching,
    isError,
    error: error instanceof Error ? error : null,
    refetch,
  };
}

export default usePatrolLocationsApi;
